import { Alert, Spinner, useToast } from '@apex/component-library'
import { Box, Icon, Link, Stack } from '@chakra-ui/react'
import { useFormik } from 'formik'
import { forIn, get, isEmpty, isEqual, omit } from 'lodash'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FaArrowLeft } from 'react-icons/fa'
import { RouteComponentProps } from 'react-router'
import { Link as ReactRouterLink } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import {
  useAccountDetails,
  useClientRect,
  useToastErrorHandler,
} from '../../hooks'
import { AccountRestrictionCodeDisplay, ToastMessage } from '../../types'
import { RoutesEnum } from '../../types/RoutesEnum'
import { InitiateTransferKeyReferenceBar } from './components/InitiateTransferKeyReferenceBar'
import { initiateTransferSchema } from './initiateTransferSchema'
import { initiateTransfer } from '../../actions'
import {
  TransferInitiationTransferType,
  TransferInitiationWebDto,
} from '../../api/types'
import {
  AccountTransferForm,
  AccountTransferPreview,
} from '../AccountTransferRequest'
import { LayoutContext } from '../../containers/contexts/layout/LayoutContext'
import { TransferInitiation } from './types'
import { currencyStringToNumber } from '../../utils/numberToCurrency'
import { toIsoCalendarDate } from '../../utils/dateUtil'

export interface MatchParams {
  accountNumber: string
}

const InitiateTransferPage = ({
  match,
  location,
  history,
}: RouteComponentProps<MatchParams>) => {
  const {
    accountDetailsState: {
      accountDetails,
      loadingResult: loadingAccountDetails,
    },
    accountOwnerState: { accountOwners, loadingAccountOwners },
    correspondentState: { correspondent, loadingResult: loadingCorrespondent },
    getAccountDetails,
    getCorrespondent,
    getAccountOwners,
  } = useAccountDetails()
  const [isPreview, setPreview] = useState(location.search === '?preview=true')
  const [, saveFormValuesToLocationState] = useState<TransferInitiation>()
  const toast = useToast()
  const { headerHeight, footerHeight } = useContext(LayoutContext)
  const [keyReferenceBarRect, keyReferenceBarRef] = useClientRect()
  const onError = useToastErrorHandler()

  useEffect(() => {
    getAccountDetails(match.params.accountNumber)
    getCorrespondent(match.params.accountNumber)
    getAccountOwners(match.params.accountNumber)
  }, [
    match.params.accountNumber,
    getAccountDetails,
    getCorrespondent,
    getAccountOwners,
  ])

  const formik = useFormik({
    initialValues: {
      account: match.params.accountNumber,
      disableAutoSign: false,
      transferType: undefined,
      contraParty: {
        account: '',
        participant: {
          participantNumber: '',
        },
        accountTitle: '',
        primarySsnOrTaxId: null,
        secondarySsnOrTaxId: null,
        comment: '',
        accountType: '',
      },
      clientReferenceId: '',
      originalControlNumber: 0,
      cash: undefined,
      assets: undefined,
      options: undefined,
    } as TransferInitiation,
    onSubmit: () => {
      history.push(`${location.pathname}?preview=true`, {
        [`initiateTransferFormValues-${accountDetails?.accountNumber}`]: formik.values,
      })
    },
    validationSchema: initiateTransferSchema,
  })

  const accountNumber = match.params.accountNumber
  const replaceHistory = useCallback(history.replace, [])

  useEffect(
    () =>
      saveFormValuesToLocationState(previousLocationState => {
        if (formik.dirty) {
          if (isEqual(previousLocationState, formik.values)) {
            return previousLocationState
          } else {
            replaceHistory(
              `/${RoutesEnum.InitiateTransfer}/${accountNumber}${
                location.search || ''
              }`,
              {
                [`initiateTransferFormValues-${accountNumber}`]: formik.values,
              }
            )
            return formik.values
          }
        } else {
          return previousLocationState
        }
      }),
    [
      formik.values,
      formik.dirty,
      accountNumber,
      replaceHistory,
      location.search,
    ]
  )

  useEffect(() => {
    const initiateTransferFormValuesKey = `initiateTransferFormValues-${accountNumber}`
    const setFormikValuesFromLocationState = () =>
      forIn(
        get(location.state, initiateTransferFormValuesKey),
        (value, field) => {
          formik.setFieldValue(field, value)
        }
      )

    if (
      location.pathname === `/${RoutesEnum.InitiateTransfer}/${accountNumber}`
    ) {
      if (!formik.dirty) {
        setFormikValuesFromLocationState()
      }
      if (location.search === '?preview=true') {
        if (!isPreview) {
          if (isEmpty(formik.errors)) {
            setPreview(true)
          } else {
            replaceHistory(location.pathname, {
              [initiateTransferFormValuesKey]: formik.values,
            })
          }
        }
      } else if (isPreview) {
        setPreview(false)
      }
    }
  }, [
    location.pathname,
    location.search,
    location.state,
    accountNumber,
    isPreview,
    formik,
    replaceHistory,
  ])

  const handleBlur = (e: { target: { name: string } }) => {
    formik.setFieldTouched(e.target.name)
  }

  const handlePreviewSubmit = () => {
    formik.submitForm().catch(() => onError('Form submission failed'))
  }

  const loading =
    loadingAccountDetails || loadingCorrespondent || loadingAccountOwners

  const handleSubmit = () => {
    const submission: TransferInitiationWebDto = {
      ...formik.values,
      clientReferenceId: formik.values.clientReferenceId.trim() || uuidv4(),
      cash:
        formik.values.transferType ===
        TransferInitiationTransferType.PartialTransferReceiver
          ? formik.values.cash?.map(cashEntry => omit(cashEntry, 'rowId'))
          : undefined,
      options:
        formik.values.transferType ===
        TransferInitiationTransferType.PartialTransferReceiver
          ? formik.values.options?.flatMap(
              ({ symbol, contractDate, put, strikePrice, isShort, amount }) => {
                if (
                  symbol &&
                  contractDate &&
                  put &&
                  strikePrice &&
                  isShort !== undefined &&
                  amount
                ) {
                  return [
                    {
                      symbol,
                      // Fall back to the raw value rather than dropping the
                      // leg; the gateway then rejects it visibly
                      contractDate:
                        toIsoCalendarDate(contractDate) ?? contractDate,
                      put,
                      strikePrice:
                        typeof strikePrice === 'string'
                          ? currencyStringToNumber(strikePrice)
                          : strikePrice,
                      isShort,
                      amount,
                    },
                  ]
                }
                return []
              }
            )
          : undefined,
      assets:
        formik.values.transferType ===
        TransferInitiationTransferType.PartialTransferReceiver
          ? formik.values.assets?.map(entry => omit(entry, 'rowId'))
          : undefined,
    }
    initiateTransfer(submission, onSuccess, onError)
  }

  const onSuccess = ({ title, description }: ToastMessage) => {
    history.replace(`/${RoutesEnum.InitiateTransfer}`)
    toast({
      title,
      description: (
        <>
          {description}
          <Box mt={2}>
            Go to{' '}
            <Link
              onClick={() =>
                history.push('/account-transfers/transfer-review/')
              }
              color="apex.link"
            >
              Account Transfer Review
            </Link>
          </Box>
        </>
      ),
      status: 'success',
      isClosable: true,
    })
  }

  return (
    <Stack direction="column" spacing={0}>
      {loading && <Spinner size="md" />}
      {!loading && accountDetails && (
        <Box>
          <Box ref={keyReferenceBarRef}>
            <InitiateTransferKeyReferenceBar accountDetails={accountDetails} />
          </Box>
          <Stack
            direction="column"
            spacing={4}
            padding={4}
            overflowY="auto"
            height={`calc(100vh - ${
              headerHeight + footerHeight + (keyReferenceBarRect?.height || 0)
            }px)`}
          >
            {isPreview ? (
              <AccountTransferPreview
                accountDetails={accountDetails}
                correspondent={correspondent}
                deliveringAccount={formik.values}
                accountOwners={accountOwners}
                onEditDetailsClicked={history.goBack}
                onSubmitClicked={handleSubmit}
              />
            ) : (
              <>
                <Link
                  as={ReactRouterLink}
                  color="apex.link"
                  to={{
                    pathname: `/${RoutesEnum.InitiateTransfer}`,
                    search: history.location.search,
                  }}
                  p="7px 0"
                >
                  <Icon as={FaArrowLeft} /> Back to Results
                </Link>
                {accountDetails?.restrictionCode && (
                  <Alert
                    status="error"
                    title="This account is restricted."
                    description={
                      AccountRestrictionCodeDisplay[
                        accountDetails?.restrictionCode
                      ]
                    }
                    isClosable={false}
                    showIcon
                  />
                )}
                <AccountTransferForm
                  accountDetails={accountDetails}
                  correspondent={correspondent}
                  deliveringAccount={formik.values}
                  // @ts-ignore
                  formikErrors={formik.errors}
                  formikTouched={formik.touched}
                  handleBlur={handleBlur}
                  handleChange={formik.handleChange}
                  setFieldValue={formik.setFieldValue}
                  handleSubmit={handlePreviewSubmit}
                  accountOwners={accountOwners}
                />
              </>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  )
}

export default InitiateTransferPage
